import { supabase } from '@/core/database/supabase';
import type { 
  AttendanceRecord, 
  AttendanceShift, 
  AttendanceStats, 
  AttendanceFilters 
} from '../types/attendance.types';

export const AttendanceService = {
  /**
   * Lấy danh sách chấm công với bộ lọc
   */
  async getAttendanceRecords(companyId: string, filters: AttendanceFilters = {}): Promise<{ data: AttendanceRecord[], total: number }> {
    let query = supabase
      .from('attendance_records')
      .select(`
        *,
        user:user_profiles(id, full_name, avatar_url:avatar_path),
        team:teams(id, name, code),
        zone:project_zones(id, zone_name, zone_code)
      `, { count: 'exact' })
      .eq('company_id', companyId);

    if (filters.projectId) query = query.eq('project_id', filters.projectId);
    if (filters.teamId) query = query.eq('team_id', filters.teamId);
    if (filters.status && filters.status !== 'all') query = query.eq('status_code', filters.status);
    
    if (filters.date) {
      query = query.eq('date', filters.date);
    } else if (filters.startDate && filters.endDate) {
      query = query.gte('date', filters.startDate).lte('date', filters.endDate);
    }

    if (filters.search) {
      // Vì join nên search full_name cần cẩn thận, ở đây search note trước
      query = query.ilike('note', `%${filters.search}%`);
    }

    query = query.order('date', { ascending: false }).order('check_in_time', { ascending: false });

    const { data, error, count } = await query;

    if (error) throw error;
    return {
      data: (data || []) as AttendanceRecord[],
      total: count || 0
    };
  },

  /**
   * Check-in: Tạo record mới hoặc cập nhật nếu đã có (trường hợp check-in lại)
   */
  async checkIn(data: Partial<AttendanceRecord>): Promise<AttendanceRecord> {
    const { data: result, error } = await supabase
      .from('attendance_records')
      .upsert([{
        ...data,
        status_code: 'present',
        check_in_time: new Date().toISOString(),
        date: new Date().toISOString().split('T')[0]
      }])
      .select()
      .single();

    if (error) throw error;
    return result as AttendanceRecord;
  },

  /**
   * Check-out: Cập nhật giờ ra và tính toán giờ công
   */
  async checkOut(id: string, lat?: number, lng?: number): Promise<AttendanceRecord> {
    const checkOutTime = new Date().toISOString();
    
    // 1. Lấy check_in_time để tính working_hours
    const { data: record, error: fetchError } = await supabase
      .from('attendance_records')
      .select('check_in_time')
      .eq('id', id)
      .single();

    if (fetchError) throw fetchError;

    let workingHours = 0;
    if (record.check_in_time) {
      const diff = new Date(checkOutTime).getTime() - new Date(record.check_in_time).getTime();
      workingHours = diff / (1000 * 60 * 60); // Đổi sang giờ
    }

    const { data, error } = await supabase
      .from('attendance_records')
      .update({
        check_out_time: checkOutTime,
        check_out_lat: lat,
        check_out_lng: lng,
        working_hours: workingHours,
        status_code: 'off_duty'
      })
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    return data as AttendanceRecord;
  },

  /**
   * Cập nhật trạng thái chấm công thủ công (bởi Foreman/Manager)
   */
  async updateAttendance(id: string, updates: Partial<AttendanceRecord>): Promise<AttendanceRecord> {
    const { data, error } = await supabase
      .from('attendance_records')
      .update(updates)
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    return data as AttendanceRecord;
  },

  /**
   * Lấy cấu hình ca làm việc
   */
  async getShifts(companyId: string): Promise<AttendanceShift[]> {
    const { data, error } = await supabase
      .from('attendance_shifts')
      .select('*')
      .eq('company_id', companyId);

    if (error) throw error;
    return data as AttendanceShift[];
  },

  /**
   * Lấy thống kê chấm công trong ngày
   */
  async getAttendanceStats(companyId: string, projectId?: string, date: string = new Date().toISOString().split('T')[0]): Promise<AttendanceStats> {
    let query = supabase
      .from('attendance_records')
      .select('status_code, working_hours')
      .eq('company_id', companyId)
      .eq('date', date);

    if (projectId) query = query.eq('project_id', projectId);

    const { data, error } = await query;

    if (error) throw error;

    const stats: AttendanceStats = {
      totalWorkers: 0, // Cần query user_profiles để lấy tổng thực tế
      presentToday: data.filter(r => ['present', 'off_duty'].includes(r.status_code)).length,
      lateToday: data.filter(r => r.status_code === 'late').length,
      onLeaveToday: data.filter(r => r.status_code === 'leave').length,
      totalManHoursMonth: data.reduce((sum, r) => sum + (r.working_hours || 0), 0),
      attendanceRate: 0,
    };

    return stats;
  }
};

